/**
 * Centralized sorting utilities for seasons and events
 * Used by both frontend and backend to ensure consistency
 */

import {
  CALENDAR_MONTHS,
  SEASONS_BY_DESCENDING_END_DATE,
  SEASON_NAMES,
  WINTER_MONTHS_CHRONOLOGICAL,
  type SeasonName,
} from './seasonDomain';

interface SortableSeason {
  name: string;
  year: number;
}

interface SortableEvent {
  month: string;
  id?: number; // For tie-breaking
}

const UNKNOWN_SORT_ORDER = 999;
const winterSeasonName = SEASON_NAMES[0];

function getSeasonSortOrder(seasonName: string): number {
  const order = SEASONS_BY_DESCENDING_END_DATE.indexOf(seasonName as SeasonName);
  return order === -1 ? UNKNOWN_SORT_ORDER : order;
}

// Oldest first, so the latest-ending season ranks last; unknown names stay after every real season.
function getChronologicalSeasonRank(seasonName: string): number {
  const order = SEASONS_BY_DESCENDING_END_DATE.indexOf(seasonName as SeasonName);
  if (order === -1) {
    return UNKNOWN_SORT_ORDER;
  }

  return SEASONS_BY_DESCENDING_END_DATE.length - 1 - order;
}

function getWinterMonthSortOrder(month: string): number {
  const order = WINTER_MONTHS_CHRONOLOGICAL.indexOf(
    month as typeof WINTER_MONTHS_CHRONOLOGICAL[number]
  );
  return order === -1 ? UNKNOWN_SORT_ORDER : order;
}

/**
 * Sort seasons by year (descending) and then by season end date (descending)
 *
 * Ordering logic:
 * - Primary: Year descending (2025 → 2024 → 2023)
 * - Secondary: Season end date descending (Winter → Fall → Summer → Spring)
 *   - Winter ends in February (latest)
 *   - Fall ends in November
 *   - Summer ends in August
 *   - Spring ends in May (earliest)
 */
export function sortSeasonsByYear<T extends SortableSeason>(seasons: T[]): T[] {
  return [...seasons].sort((a, b) => {
    if (a.year !== b.year) {
      return b.year - a.year;
    }

    return getSeasonSortOrder(a.name) - getSeasonSortOrder(b.name);
  });
}

/**
 * Sort seasons oldest first: year ascending, then season end date ascending
 * (Spring → Summer → Fall → Winter), unknown names last within a year.
 */
export function sortSeasonsChronologically<T extends SortableSeason>(seasons: T[]): T[] {
  return [...seasons].sort((a, b) => {
    if (a.year !== b.year) {
      return a.year - b.year;
    }

    return getChronologicalSeasonRank(a.name) - getChronologicalSeasonRank(b.name);
  });
}

/**
 * Sort events by month with special handling for Winter seasons
 *
 * Winter ordering: December → January → February (cross-year chronological)
 * Other seasons: Standard calendar month order
 */
export function sortEventsByMonth<T extends SortableEvent>(
  events: T[],
  seasonName?: string
): T[] {
  return [...events].sort((a, b) => {
    if (seasonName === winterSeasonName) {
      const orderA = getWinterMonthSortOrder(a.month);
      const orderB = getWinterMonthSortOrder(b.month);

      if (orderA !== orderB) {
        return orderA - orderB;
      }
    } else {
      const monthIdxA = CALENDAR_MONTHS.indexOf(a.month as typeof CALENDAR_MONTHS[number]);
      const monthIdxB = CALENDAR_MONTHS.indexOf(b.month as typeof CALENDAR_MONTHS[number]);

      if (monthIdxA !== monthIdxB) {
        return monthIdxA - monthIdxB;
      }
    }

    if (a.id !== undefined && b.id !== undefined) {
      return a.id - b.id;
    }

    return 0;
  });
}
